// BizPlus parity Phase 3.1 — integration configuration.
//
// One screen for every provider. The form is rendered from the server's field
// definitions, not hardcoded per provider, so adding an integration needs no
// frontend change at all.
//
// Secrets are never sent to the browser: a stored secret arrives as `true` and
// renders as "•••••••• (saved)". Leaving such a field untouched keeps the stored
// value; typing in it replaces it. That is why the input starts empty even for a
// provider that is fully configured.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Plug, Check, X, AlertTriangle, ShieldCheck, Trash2, RefreshCw, Inbox, Copy,
} from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { SettingsSubNav } from '../components/SettingsSubNav';

interface FieldDef {
  name: string;
  label: string;
  type: 'text' | 'password' | 'number' | 'boolean';
  secret: boolean;
  required: boolean;
  placeholder?: string;
  help?: string;
}

interface ProviderItem {
  provider: string;
  label: string;
  group: string;
  delivery: 'poll' | 'webhook' | 'outbound';
  capabilities: string[];
  fields: FieldDef[];
  configured: boolean;
  isActive: boolean;
  credentials: Record<string, unknown>;
  missing: { name: string; label: string }[];
  pollIntervalMinutes: number;
  minPollMinutes: number;
  autoCreateEnquiry: boolean;
  defaultAssigneeId: string | null;
  lastSyncAt: string | null;
  lastError: string;
  consecutiveFailures: number;
  pendingLeads: number;
}

const DELIVERY_LABEL: Record<string, string> = {
  poll: 'Polled on a schedule',
  webhook: 'Provider calls us',
  outbound: 'Outbound only',
};

export function IntegrationsSettingsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  // Only what the admin actually typed, so an untouched secret is never posted.
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [pollDraft, setPollDraft] = useState<number | null>(null);
  const [autoCreateDraft, setAutoCreateDraft] = useState<boolean | null>(null);
  const [defaultAssigneeDraft, setDefaultAssigneeDraft] = useState<string | null>(null);

  // Auto-created leads need SOMEONE to own them (Lead.user_id is required) —
  // this is that "someone", picked once per provider rather than per lead.
  const users = useQuery({
    queryKey: ['users', 'list'],
    queryFn: async () => (await api.get<{ users: { id: string; display_name: string; department: string }[] }>('/api/users')).data.users,
    staleTime: 5 * 60_000,
  });

  const list = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => (await api.get<{
      groups: string[]; items: ProviderItem[]; encryptionAvailable: boolean;
    }>('/api/integrations')).data,
  });

  const current = useMemo(
    () => list.data?.items.find((i) => i.provider === selected) || null,
    [list.data, selected],
  );

  const refresh = () => qc.invalidateQueries({ queryKey: ['integrations'] });
  const reset = () => { setDraft({}); setPollDraft(null); setAutoCreateDraft(null); setDefaultAssigneeDraft(null); };

  const save = useMutation({
    mutationFn: async (extra: Record<string, unknown> = {}) => (await api.put(
      `/api/integrations/${selected}`,
      {
        credentials: draft,
        ...(pollDraft !== null ? { pollIntervalMinutes: pollDraft } : {}),
        ...(autoCreateDraft !== null ? { autoCreateEnquiry: autoCreateDraft } : {}),
        ...(defaultAssigneeDraft !== null ? { defaultAssigneeId: defaultAssigneeDraft || null } : {}),
        ...extra,
      },
    )).data,
    onSuccess: () => { refresh(); reset(); toast.success('Saved.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save.')),
  });

  const toggle = useMutation({
    mutationFn: async () => (await api.post(`/api/integrations/${selected}/toggle`)).data,
    onSuccess: (d: any) => { refresh(); toast.success(d.isActive ? 'Enabled.' : 'Disabled.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not change status.')),
  });

  const check = useMutation({
    mutationFn: async () => (await api.post(`/api/integrations/${selected}/test`)).data,
    onSuccess: (d: any) => {
      if (d.ok) toast.success('Configuration looks complete.');
      else if (!d.decryptable) toast.error(`Credentials cannot be read: ${d.decryptError}`);
      else toast.error(`Still needed: ${d.missing.map((m: any) => m.label).join(', ')}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Check failed.')),
  });

  const clear = useMutation({
    mutationFn: async () => (await api.delete(`/api/integrations/${selected}`)).data,
    onSuccess: () => { refresh(); reset(); toast.success('Credentials cleared.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not clear.')),
  });

  return (
    <div>
      <SettingsSubNav />
      <div className="max-w-[1580px] mx-auto space-y-4 pb-16">
        {list.data && !list.data.encryptionAvailable && (
          <Card>
            <CardBody className="flex items-start gap-3 text-sm">
              <AlertTriangle className="h-5 w-5 shrink-0 text-warning-text" />
              <div>
                <p className="font-semibold">Credential storage is not configured.</p>
                <p className="text-muted-foreground">
                  Set <code>INTEGRATION_ENCRYPTION_KEY</code> (or <code>JWT_SECRET</code>) on the server.
                  Until then saving is refused rather than storing passwords in plain text.
                </p>
              </div>
            </CardBody>
          </Card>
        )}

        <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
          <Card>
            <CardHeader title="Integrations" />
            <CardBody className="p-0">
              {list.isLoading ? (
                <div className="p-4"><Skeleton className="h-64" /></div>
              ) : (
                (list.data?.groups ?? []).map((group) => {
                  const items = (list.data?.items ?? []).filter((i) => i.group === group);
                  if (!items.length) return null;
                  return (
                    <div key={group}>
                      <p className="bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {group}
                      </p>
                      <ul className="divide-y">
                        {items.map((i) => (
                          <li key={i.provider}>
                            <button
                              type="button"
                              onClick={() => { setSelected(i.provider); reset(); }}
                              className={`flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-muted/30 ${
                                i.provider === selected ? 'bg-primary/5' : ''
                              }`}
                            >
                              <Plug className="h-4 w-4 shrink-0 text-primary" />
                              <span className="min-w-0 flex-1 truncate text-sm font-medium">{i.label}</span>
                              {i.pendingLeads > 0 && (
                                <span className="rounded-full bg-info/15 px-1.5 text-xs text-info-text" title={`${i.pendingLeads} unprocessed`}>
                                  {i.pendingLeads}
                                </span>
                              )}
                              {/* Wrapped in a span: lucide icons do not accept
                                  a `title` prop, and the tooltip is what makes
                                  a bare tick/cross legible. */}
                              {i.isActive ? (
                                <span title="Enabled" className="shrink-0">
                                  <Check className="h-3.5 w-3.5 text-success-text" />
                                </span>
                              ) : i.configured ? (
                                <span title="Configured but disabled" className="shrink-0">
                                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                                </span>
                              ) : null}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })
              )}
            </CardBody>
          </Card>

          {!current ? (
            <Card><CardBody>
              <p className="text-sm text-muted-foreground">
                Pick an integration to configure it. Credentials are encrypted before they are
                stored and are never sent back to this screen — a saved secret shows as
                “saved” and keeps its value unless you type a new one.
              </p>
            </CardBody></Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardHeader
                  title={current.label}
                  action={
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="ghost" onClick={() => check.mutate()} disabled={!current.configured}>
                        <ShieldCheck className="h-4 w-4 mr-1" /> Check
                      </Button>
                      <Button
                        size="sm"
                        variant={current.isActive ? 'ghost' : 'secondary'}
                        onClick={() => toggle.mutate()}
                        disabled={!current.configured || toggle.isPending}
                      >
                        {current.isActive ? 'Disable' : 'Enable'}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive"
                        disabled={!current.configured}
                        onClick={() => window.confirm(`Clear ${current.label} credentials?`) && clear.mutate()}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  }
                />
                <CardBody className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    {DELIVERY_LABEL[current.delivery]} · {current.capabilities.join(', ')}
                  </p>
                  {current.missing.length > 0 && (
                    <p className="flex items-center gap-2 text-warning-text">
                      <AlertTriangle className="h-4 w-4" />
                      Still needed: {current.missing.map((m) => m.label).join(', ')}
                    </p>
                  )}
                  {current.lastError && (
                    <p className="text-destructive">
                      Last error: {current.lastError}
                      {current.consecutiveFailures > 1 && ` (${current.consecutiveFailures} in a row)`}
                    </p>
                  )}
                  {current.lastSyncAt && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <RefreshCw className="h-3.5 w-3.5" />
                      Last synced {new Date(current.lastSyncAt).toLocaleString('en-IN')}
                    </p>
                  )}
                  {current.pendingLeads > 0 && (
                    <p className="flex items-center gap-2 text-info-text">
                      <Inbox className="h-4 w-4" /> {current.pendingLeads} received record(s) not yet processed
                    </p>
                  )}
                </CardBody>
              </Card>

              {current.delivery === 'webhook' && (
                <Card>
                  <CardHeader title="Webhook URL" />
                  <CardBody className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {current.provider === 'website'
                        ? 'Set this as ZYNX_BASE_URL (the base — not this full path) in the website\'s environment, so its forms reach this endpoint.'
                        : 'Give this URL to the provider so it can send leads here.'}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 truncate rounded-md border bg-muted/30 px-3 py-2 text-xs">
                        {`${window.location.origin}/api/integrations/hooks/${current.provider}`}
                      </code>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/api/integrations/hooks/${current.provider}`);
                          toast.success('Copied.');
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardHeader title="Credentials" />
                <CardBody className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    {current.fields.map((field) => {
                      const stored = current.credentials[field.name];
                      const isSetSecret = field.secret && stored === true;

                      if (field.type === 'boolean') {
                        return (
                          <label key={field.name} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={Boolean(draft[field.name] ?? stored)}
                              onChange={(e) => setDraft((d) => ({ ...d, [field.name]: e.target.checked }))}
                            />
                            <span>{field.label}</span>
                          </label>
                        );
                      }

                      return (
                        <div key={field.name}>
                          <Input
                            label={`${field.label}${field.required ? ' *' : ''}`}
                            type={field.secret ? 'password' : field.type === 'number' ? 'number' : 'text'}
                            // A saved secret is intentionally NOT prefilled — the
                            // value does not exist client-side to prefill with.
                            placeholder={isSetSecret ? '•••••••• (saved — leave blank to keep)' : field.placeholder}
                            value={
                              field.secret
                                ? String(draft[field.name] ?? '')
                                : String(draft[field.name] ?? (stored ?? ''))
                            }
                            onChange={(e) => setDraft((d) => ({
                              ...d,
                              [field.name]: field.type === 'number' ? Number(e.target.value) : e.target.value,
                            }))}
                          />
                          {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
                        </div>
                      );
                    })}
                  </div>

                  {current.delivery === 'poll' && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Input
                          label="Poll every (minutes)"
                          type="number"
                          min={current.minPollMinutes}
                          value={String(pollDraft ?? current.pollIntervalMinutes)}
                          onChange={(e) => setPollDraft(Number(e.target.value))}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          Minimum {current.minPollMinutes} minutes — polling faster risks the provider
                          throttling this key.
                        </p>
                      </div>
                    </div>
                  )}

                  {current.capabilities.includes('leads') && (
                    <div className="space-y-3 border-t pt-3">
                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={autoCreateDraft ?? current.autoCreateEnquiry}
                          onChange={(e) => setAutoCreateDraft(e.target.checked)}
                        />
                        <span>
                          <span className="font-medium">Create Leads automatically</span>
                          <p className="text-xs text-muted-foreground">
                            {current.delivery === 'webhook'
                              ? 'Off: every submission lands in the Lead Inbox to review and move by hand. On: it becomes a real Lead the moment it arrives — no review step.'
                              : 'Off: a poll only stages records in the Lead Inbox. On: staged records are promoted to Leads automatically after each poll.'}
                          </p>
                        </span>
                      </label>

                      {(autoCreateDraft ?? current.autoCreateEnquiry) && (
                        <div>
                          <label className="text-sm font-medium">Assign auto-created Leads to</label>
                          <select
                            className="mt-1 w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm"
                            value={defaultAssigneeDraft ?? current.defaultAssigneeId ?? ''}
                            onChange={(e) => setDefaultAssigneeDraft(e.target.value)}
                          >
                            <option value="">— Not set —</option>
                            {(users.data ?? []).map((u) => (
                              <option key={u.id} value={u.id}>{u.display_name}{u.department ? ` (${u.department})` : ''}</option>
                            ))}
                          </select>
                          {!(defaultAssigneeDraft ?? current.defaultAssigneeId) && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-warning-text">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              Required for auto-create to actually work — every Lead needs an owner.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
                    <Button variant="outline" onClick={reset} disabled={!Object.keys(draft).length && pollDraft === null && autoCreateDraft === null && defaultAssigneeDraft === null}>
                      Discard changes
                    </Button>
                    <Button onClick={() => save.mutate({})} disabled={save.isPending}>
                      {save.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
